import {
  put,
  takeLatest,
  takeEvery,
  select,
  all,
  take,
} from 'redux-saga/effects';
import _ from 'lodash';
import {
  TeammoApi,
  normalize,
  compilePaginationObject,
} from 'app/utils/redux/helpers';
import { purchaseSchema, purchaseListSchema } from 'app/utils/redux/schemas';
import { handleError } from 'app/utils/handlers/handleError';
import { loadingStart, loadingFinish } from 'app/ducks/loading/actions';
import { sagaActions, createPurchase } from 'app/ducks/purchases/actions';
import { PurchasesActionTypes as types } from 'app/ducks/purchases/types';
import { createCreditCard } from 'app/ducks/creditCards/actions';
import { getCurrentCreditCard } from 'app/ducks/creditCards/selectors';
import { CreditCardsActionTypes } from 'app/ducks/creditCards/types';

function* createPurchaseSaga(action): any {
  yield put(sagaActions.createPurchasePending(action.payload));
  const [error, response] = yield (yield TeammoApi(yield select())).purchases.create(
    action.payload,
  );

  if (response) {
    const normalizedResponse = normalize(response, purchaseSchema);

    yield put(sagaActions.createPurchaseSuccess(normalizedResponse));
  } else {
    yield put(
      handleError(
        error,
        sagaActions.createPurchaseFailed(_.isEmpty(error) ? {} : error.response.data.errors),
      ),
    );
  }
}

function* createCreditCardAndPurchaseSaga(action): any {
  yield put(yield createCreditCard(action.payload.cardPayload));
  yield take(CreditCardsActionTypes.CREATE_CREDIT_CARD_SUCCESS);
  const creditCard = getCurrentCreditCard(yield select());
  if (!_.isEmpty(creditCard)) {
    yield put(
      yield createPurchase({
        ...action.payload.purchasePayload,
        creditCard: {
          id: creditCard.id,
        },
      }),
    );
    yield put(sagaActions.createCreditCardAndPurchaseSuccess());
  } else {
    yield put(sagaActions.createCreditCardAndPurchaseFailed());
  }
}

function* updatePurchaseSaga(action): any {
  yield put(sagaActions.updatePurchasePending(action.payload));
  const [error, response] = yield (yield TeammoApi(yield select())).purchases.update(
    action.payload,
  );

  if (response) {
    const normalizedResponse = normalize(response, purchaseSchema);

    yield put(sagaActions.updatePurchaseSuccess(normalizedResponse));
  } else {
    yield put(
      handleError(
        error,
        sagaActions.updatePurchaseFailed(_.isEmpty(error) ? {} : error.response.data.errors),
      ),
    );
  }
}

function* getPurchaseSaga(action): any {
  yield put(sagaActions.getPurchasePending());
  const [error, response] = yield (yield TeammoApi(yield select())).purchases.show(
    action.payload,
  );
  if (response) {
    const normalizedResponse = normalize(response, purchaseSchema);
    yield put(sagaActions.getPurchaseSuccess(normalizedResponse));
    return normalizedResponse.entities.purchases[normalizedResponse.result];
  }
  yield put(
    handleError(
      error,
      sagaActions.getPurchaseFailed(_.isEmpty(error) ? {} : error.response.data.errors),
    ),
  );
  return null;
}

function* getAllPurchasesSaga(action): any {
  yield put(
    sagaActions.getAllPurchasesPending(action.payload.compile ? action.payload.pagination : {}),
  );
  yield put(loadingStart());

  const paginationObject = action.payload.compile
    ? compilePaginationObject(yield select((state) => state.purchases.pagination))
    : action.payload.pagination;

  const [error, response] = yield (yield TeammoApi(yield select())).purchases.list(
    paginationObject,
  );

  if (response) {
    const normalizedResponse = normalize(response, purchaseListSchema);

    yield put(
      sagaActions.getAllPurchasesSuccess(normalizedResponse, response.meta),
    );
    yield put(loadingFinish());
  } else {
    yield put(
      error,
      sagaActions.getAllPurchasesFailed(_.isEmpty(error) ? {} : error.response.data.errors),
    );
  }
}

/* ********************************* SAGA WATCHERS ********************************* */

function* createPurchaseSagaWatcher(): any {
  yield takeEvery(types.CREATE_PURCHASE_REQUESTED, createPurchaseSaga);
}

function* createCreditCardAndPurchaseSagaWatcher(): any {
  yield takeEvery(
    types.CREATE_CREDIT_CARD_AND_PURCHASE_REQUESTED,
    createCreditCardAndPurchaseSaga,
  );
}

function* updatePurchaseSagaWatcher(): any {
  yield takeLatest(types.UPDATE_PURCHASE_REQUESTED, updatePurchaseSaga);
}

function* getPurchaseSagaWatcher(): any {
  yield takeEvery(types.GET_PURCHASE_REQUESTED, getPurchaseSaga);
}

function* getAllPurchasesSagaWatcher(): any {
  yield takeEvery(types.GET_ALL_PURCHASES_REQUESTED, getAllPurchasesSaga);
}

export default function* root(): any {
  yield all([
    createPurchaseSagaWatcher(),
    createCreditCardAndPurchaseSagaWatcher(),
    updatePurchaseSagaWatcher(),
    getPurchaseSagaWatcher(),
    getAllPurchasesSagaWatcher(),
  ]);
}
